import { useEffect, useState } from "react";
import { ApiError, type Account, type TillDay, api, formatMoney } from "./api";
import { singaporeToday } from "./dates";
import { SelectField } from "./fields";
import { Alert, Badge, Box, Empty, Table, Th, TwoLine, Working } from "./ui";

/**
 * Opening and closing the drawer (quotation E1).
 *
 * A bank sends statements; a drawer sends nothing. So this shows what the books say
 * should be in it, somebody counts what is actually in it, and the difference is written
 * in with whatever explanation there is. From then on the books agree with the drawer and
 * the gap is still on the record, which is the opposite of how cash usually goes missing.
 */
export function Till({
  accounts,
  canBank = false,
  onChanged,
}: {
  readonly accounts: readonly Account[];
  /** Taking cash to the bank, which is an admin's: the server asks for account.manage. */
  readonly canBank?: boolean;
  /** Told when the drawer moves, so the balances above it follow at once. */
  readonly onChanged?: (() => void) | undefined;
}): React.ReactElement {
  const tills = accounts.filter((account) => account.kind === "cash" && account.ownerKind === "company" && account.isActive);
  const [accountId, setAccountId] = useState(tills[0]?.id ?? "");
  const [day, setDay] = useState(singaporeToday());
  const [till, setTill] = useState<TillDay | undefined>();
  const [counted, setCounted] = useState("");
  const [note, setNote] = useState("");
  const [banking, setBanking] = useState("");
  const [problem, setProblem] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function reload(): Promise<void> {
    if (!accountId) return;
    try {
      const result = await api.till(accountId, day);
      setTill(result.till);
      setProblem(undefined);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not load the cash drawer");
    }
  }

  useEffect(() => {
    void reload();
  }, [accountId, day]);

  async function run(work: () => Promise<string>): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      setNotice(await work());
      await reload();
      onChanged?.();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "The action could not be completed");
    } finally {
      setBusy(false);
    }
  }

  if (tills.length === 0) {
    return (
      <Box title="Cash drawer">
        <Empty icon="ri-wallet-3-line">No company cash account has been set up yet.</Empty>
      </Box>
    );
  }

  const countedCents = Math.round(Number(counted.replace(/[^\d.]/g, "")) * 100);
  const difference = till && Number.isFinite(countedCents) ? countedCents - till.expectedCents : 0;

  return (
    <Box title="Cash drawer">
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      {notice ? (
        <Alert tone="success" onClose={() => setNotice(undefined)}>
          {notice}
        </Alert>
      ) : null}

      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="till-account" className="form-label">
            Cash drawer
          </label>
          <SelectField
            id="till-account"
            value={accountId}
            options={tills.map((account) => ({ value: account.id, label: account.name }))}
            onChange={setAccountId}
          />
        </div>
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="till-day" className="form-label">
            Business date
          </label>
          <input
            id="till-day"
            type="date"
            className="form-control"
            value={day}
            max={singaporeToday()}
            onChange={(changed) => setDay(changed.target.value)}
          />
        </div>
      </div>

      {till ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center py-3 border-y border-defaultborder dark:border-defaultborder/10">
            <Figure label="Opening cash balance" value={till.openingCents} />
            <Figure label="Cash received" value={till.takenCents} />
            <Figure label="Cash paid out and removed for deposit" value={till.paidOutCents + till.bankedCents} />
            <div>
              <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">Expected cash balance</p>
              <p className="font-semibold text-[1.25rem] mb-0">{formatMoney(till.expectedCents)}</p>
            </div>
          </div>

          {till.count ? (
            <div className="mt-4">
              <p className="mb-1 flex items-center gap-2">
                <Badge tone={till.count.differenceCents === 0 ? "success" : "warning"}>
                  {till.count.differenceCents === 0
                    ? "Balanced"
                    : `${till.count.differenceCents > 0 ? "Over" : "Short"} by ${formatMoney(Math.abs(till.count.differenceCents))}`}
                </Badge>
                <span className="font-semibold">{formatMoney(till.count.countedCents)} counted</span>
              </p>
              <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-0">
                Counted by {till.count.countedBy}
                {till.count.note ? ` · ${till.count.note}` : ""}
              </p>
            </div>
          ) : (
            <div className="mt-4">
              <p className="font-semibold mb-2">Cash closing</p>
              <div className="grid grid-cols-12 gap-3 items-end">
                <div className="sm:col-span-3 col-span-12">
                  <label htmlFor="till-counted" className="form-label">
                    Actual cash counted
                  </label>
                  <input
                    id="till-counted"
                    type="text"
                    inputMode="decimal"
                    className="form-control !text-[1.125rem] font-semibold"
                    placeholder="0.00"
                    value={counted}
                    onChange={(changed) => setCounted(changed.target.value)}
                  />
                </div>
                <div className="sm:col-span-5 col-span-12">
                  <label htmlFor="till-note" className="form-label">
                    {difference === 0 ? "Note (optional)" : "Reason for difference"}
                  </label>
                  <input
                    id="till-note"
                    type="text"
                    className="form-control"
                    placeholder={
                      difference === 0
                        ? "Notes for the next person"
                        : "Brief reason, such as incorrect change given"
                    }
                    value={note}
                    onChange={(changed) => setNote(changed.target.value)}
                  />
                </div>
                <div className="sm:col-span-4 col-span-12">
                  <button
                    type="button"
                    className="ti-btn ti-btn-primary-full !mb-0 w-full"
                    disabled={busy || counted.trim() === ""}
                    onClick={() =>
                      void run(async () => {
                        const result = await api.countTill(accountId, day, countedCents, note.trim() || null);
                        setCounted("");
                        setNote("");
                        return result.result.differenceCents === 0
                          ? "Cash closing complete. The drawer balances."
                          : `Cash closing complete. The drawer is ${result.result.differenceCents > 0 ? "over" : "short"} by ${formatMoney(Math.abs(result.result.differenceCents))}, and the difference has been recorded.`;
                      })
                    }
                  >
                    {busy ? <Working label="Closing" /> : "Complete cash closing"}
                  </button>
                </div>
              </div>
              {counted.trim() !== "" && difference !== 0 ? (
                <p className={`text-[0.813rem] mt-2 mb-0 ${difference > 0 ? "text-info" : "text-warning"}`}>
                  {difference > 0 ? "Over" : "Short"} by {formatMoney(Math.abs(difference))}. Enter a reason for the
                  difference; the records will be adjusted to match the actual cash counted.
                </p>
              ) : null}
            </div>
          )}

          {canBank ? (
          <div className="mt-5">
            <p className="font-semibold mb-2">Cash removed for bank deposit</p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="till-bank" className="form-label">
                  Amount removed (S$)
                </label>
                <input
                  id="till-bank"
                  type="text"
                  inputMode="decimal"
                  className="form-control w-[10rem]"
                  placeholder="0.00"
                  value={banking}
                  onChange={(changed) => setBanking(changed.target.value)}
                />
              </div>
              <button
                type="button"
                className="ti-btn ti-btn-light !mb-0"
                disabled={busy || banking.trim() === ""}
                onClick={() =>
                  void run(async () => {
                    const amount = Math.round(Number(banking.replace(/[^\d.]/g, "")) * 100);
                    await api.bankCash(accountId, day, amount);
                    setBanking("");
                    return `${formatMoney(amount)} removed from the cash drawer. The deposit will appear on the bank statement once the bank records it.`;
                  })
                }
              >
                <i className="ri-bank-line" /> Record cash removal
              </button>
              <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0 flex-1 min-w-[14rem]">
                This reduces the cash drawer balance only. The bank side is recorded from the bank statement and matched
                separately.
              </p>
            </div>
          </div>
          ) : null}

          <div className="mt-5">
            <p className="font-semibold mb-2">Cash drawer transactions</p>
            {till.lines.length === 0 ? (
              <Empty icon="ri-wallet-3-line">No cash drawer transactions on this date.</Empty>
            ) : (
              <Table
                head={
                  <>
                    <Th>Description</Th>
                    <Th end>Amount</Th>
                    <Th>Recorded by</Th>
                  </>
                }
              >
                {till.lines.map((line) => (
                  <tr key={line.id} className="border-b border-defaultborder">
                    <td>
                      <TwoLine main={line.description} sub={line.reason ?? KINDS[line.kind]} />
                    </td>
                    <td className={`!text-end font-semibold ${line.amountCents < 0 ? "text-danger" : ""}`}>
                      {formatMoney(line.amountCents)}
                    </td>
                    <td className="text-[#8c9097] dark:text-white/50">
                      {line.who ?? "-"}
                      {line.deviceCode ? ` · ${line.deviceCode}` : ""}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </>
      ) : null}
    </Box>
  );
}

const KINDS: Record<string, string> = {
  sale: "Sale",
  sale_reversed: "Sale reversed",
  buyback: "Customer buyback",
  voucher: "Payment voucher paid",
  purchase: "Supplier purchase paid",
  banked: "Removed for bank deposit",
  float_in: "Float added",
  count: "End-of-day count",
  correction: "Correction",
};

function Figure({ label, value }: { readonly label: string; readonly value: number }): React.ReactElement {
  return (
    <div>
      <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">{label}</p>
      <p className="font-semibold text-[1.125rem] mb-0">{formatMoney(value)}</p>
    </div>
  );
}
